import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, LogOut, Package, CalendarDays,
  Settings as SettingsIcon, ShoppingCart, Mail, X, Upload, Ticket, Star, AtSign,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { money } from "../lib/format";
import TopNav from "../components/TopNav";

const TABS = [
  { key: "products", label: "Productos", icon: Package },
  { key: "events", label: "Eventos", icon: CalendarDays },
  { key: "testimonials", label: "Reseñas", icon: Star },
  { key: "coupons", label: "Cupones", icon: Ticket },
  { key: "orders", label: "Pedidos", icon: ShoppingCart },
  { key: "messages", label: "Mensajes", icon: Mail },
  { key: "subscribers", label: "Suscriptores", icon: AtSign },
  { key: "settings", label: "Ajustes", icon: SettingsIcon },
];

const SCENT_OPTIONS = [
  { key: "floral", label: "Floral" },
  { key: "dulce", label: "Dulce" },
  { key: "fresco", label: "Fresco" },
  { key: "amaderado", label: "Amaderado" },
];

const CATEGORIES = [
  { key: "mini-perfumes", label: "Mini Perfumes" },
  { key: "carteras", label: "Carteras" },
  { key: "accesorios", label: "Accesorios" },
];

const emptyProduct = {
  name: "", description: "", category: "mini-perfumes", price: "", gender: "unisex",
  image: "", images: [], scents: [], is_offer: false, offer_price: "", is_upcoming: false,
  is_giftset: false, featured: false, stock: 99, order: 0,
};
const emptyEvent = { title: "", description: "", date: "", location: "", image: "", order: 0 };
const emptyCoupon = { code: "", type: "percent", value: "", active: true, min_total: 0 };
const emptyTestimonial = { name: "", text: "", rating: 5, location: "", image: "", active: true, order: 0 };

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.15em] text-taupe">{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

const inputCls =
  "w-full bg-cream border border-line rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-rose transition-colors";

function ImageUpload({ value, onChange, testid }) {
  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("La imagen debe pesar menos de 2MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result);
    reader.readAsDataURL(file);
  };
  return (
    <div className="flex gap-3 items-start">
      <div className="w-20 h-24 bg-surface border border-line rounded-lg overflow-hidden shrink-0 flex items-center justify-center">
        {value ? (
          <img src={value} alt="preview" className="w-full h-full object-cover" />
        ) : (
          <Upload size={18} className="text-taupe" />
        )}
      </div>
      <div className="flex-1 space-y-2">
        <input
          data-testid={`${testid}-url`}
          value={value?.startsWith("data:") ? "" : value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Pega una URL de imagen..."
          className={inputCls}
        />
        <label className="inline-flex items-center gap-2 text-xs text-rose cursor-pointer hover:text-rose-hover">
          <Upload size={13} /> Subir foto desde el dispositivo
          <input
            data-testid={`${testid}-file`}
            type="file"
            accept="image/*"
            onChange={handleFile}
            className="hidden"
          />
        </label>
      </div>
    </div>
  );
}

function MultiImageUpload({ values, onChange, testid }) {
  const list = values || [];
  const addUrl = () => onChange([...list, ""]);
  const setAt = (i, v) => onChange(list.map((x, idx) => (idx === i ? v : x)));
  const removeAt = (i) => onChange(list.filter((_, idx) => idx !== i));
  const handleFile = (i, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("La imagen debe pesar menos de 2MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setAt(i, reader.result);
    reader.readAsDataURL(file);
  };
  return (
    <div className="space-y-2">
      {list.map((v, i) => (
        <div key={i} className="flex gap-2 items-center">
          <div className="w-12 h-14 bg-surface border border-line rounded overflow-hidden shrink-0">
            {v && <img src={v} alt="" className="w-full h-full object-cover" />}
          </div>
          <input
            data-testid={`${testid}-url-${i}`}
            value={v?.startsWith("data:") ? "" : v}
            onChange={(e) => setAt(i, e.target.value)}
            placeholder={v?.startsWith("data:") ? "(imagen subida)" : "URL de la foto..."}
            className={inputCls}
          />
          <label className="text-xs text-rose cursor-pointer shrink-0">
            <Upload size={15} />
            <input type="file" accept="image/*" onChange={(e) => handleFile(i, e)} className="hidden" />
          </label>
          <button onClick={() => removeAt(i)} className="text-taupe hover:text-rose shrink-0">
            <X size={16} />
          </button>
        </div>
      ))}
      <button
        data-testid={`${testid}-add`}
        onClick={addUrl}
        type="button"
        className="text-xs text-rose inline-flex items-center gap-1 hover:text-rose-hover"
      >
        <Plus size={13} /> Añadir foto a la galería
      </button>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [events, setEvents] = useState([]);
  const [orders, setOrders] = useState([]);
  const [messages, setMessages] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [modal, setModal] = useState(null); // {type:'product'|'event'|'coupon'|'testimonial', data}

  const load = useCallback(async () => {
    try {
      const [p, e, s] = await Promise.all([
        api.get("/products"),
        api.get("/events"),
        api.get("/settings"),
      ]);
      setProducts(p.data);
      setEvents(e.data);
      setSettings(s.data);
    } catch {}
  }, []);

  useEffect(() => {
    if (user === false) navigate("/admin/login");
    else if (user && user.must_change_password) navigate("/admin/cambiar-clave");
    else if (user && user.role === "cajero") navigate("/admin/pos");
  }, [user, navigate]);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  useEffect(() => {
    if (!user) return;
    if (tab === "orders") api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    if (tab === "messages") api.get("/admin/contacts").then((r) => setMessages(r.data)).catch(() => {});
    if (tab === "coupons") api.get("/admin/coupons").then((r) => setCoupons(r.data)).catch(() => {});
    if (tab === "testimonials") api.get("/admin/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    if (tab === "subscribers") api.get("/admin/subscribers").then((r) => setSubscribers(r.data)).catch(() => {});
  }, [tab, user]);

  const reloadCoupons = () => api.get("/admin/coupons").then((r) => setCoupons(r.data)).catch(() => {});
  const reloadTestimonials = () => api.get("/admin/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});

  if (!user) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <span className="font-display text-4xl tracking-widest animate-pulse">MO</span>
      </div>
    );
  }

  const currency = settings?.currency || "RD$";
  const readOnly = user.role === "visor";

  // ---- product/event save ----
  const saveProduct = async (data) => {
    const payload = {
      ...data,
      price: Number(data.price) || 0,
      offer_price: data.offer_price ? Number(data.offer_price) : null,
      stock: Number(data.stock) || 0,
      order: Number(data.order) || 0,
      images: (data.images || []).filter(Boolean),
      is_giftset: !!data.is_giftset,
      scents: data.scents || [],
      gender: data.gender || "unisex",
    };
    try {
      if (data.id) await api.put(`/admin/products/${data.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Producto guardado");
      setModal(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveEvent = async (data) => {
    const payload = { ...data, order: Number(data.order) || 0 };
    try {
      if (data.id) await api.put(`/admin/events/${data.id}`, payload);
      else await api.post("/admin/events", payload);
      toast.success("Evento guardado");
      setModal(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const delProduct = async (id) => {
    if (!window.confirm("¿Eliminar este producto?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Producto eliminado");
    load();
  };
  const quickSetGender = async (p, gender) => {
    try {
      await api.put(`/admin/products/${p.id}`, {
        ...p, price: Number(p.price) || 0, offer_price: p.offer_price ?? null,
        stock: Number(p.stock) || 0, order: Number(p.order) || 0,
        images: (p.images || []).filter(Boolean), scents: p.scents || [], gender,
      });
      toast.success("Género actualizado");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delEvent = async (id) => {
    if (!window.confirm("¿Eliminar este evento?")) return;
    await api.delete(`/admin/events/${id}`);
    toast.success("Evento eliminado");
    load();
  };

  const saveCoupon = async (data) => {
    const payload = {
      code: data.code, type: data.type,
      value: Number(data.value) || 0, active: !!data.active,
      min_total: Number(data.min_total) || 0,
    };
    try {
      if (data.id) await api.put(`/admin/coupons/${data.id}`, payload);
      else await api.post("/admin/coupons", payload);
      toast.success("Cupón guardado");
      setModal(null);
      reloadCoupons();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const delCoupon = async (id) => {
    if (!window.confirm("¿Eliminar este cupón?")) return;
    await api.delete(`/admin/coupons/${id}`);
    toast.success("Cupón eliminado");
    reloadCoupons();
  };

  const saveTestimonial = async (data) => {
    const payload = {
      name: data.name, text: data.text, rating: Number(data.rating) || 5,
      location: data.location || "", image: data.image || "",
      active: !!data.active, order: Number(data.order) || 0,
    };
    try {
      if (data.id) await api.put(`/admin/testimonials/${data.id}`, payload);
      else await api.post("/admin/testimonials", payload);
      toast.success("Reseña guardada");
      setModal(null);
      reloadTestimonials();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const delTestimonial = async (id) => {
    if (!window.confirm("¿Eliminar esta reseña?")) return;
    await api.delete(`/admin/testimonials/${id}`);
    toast.success("Reseña eliminada");
    reloadTestimonials();
  };

  const saveSettings = async () => {
    try {
      await api.put("/admin/settings", {
        whatsapp_number: settings.whatsapp_number,
        contact_email: settings.contact_email,
        instagram_url: settings.instagram_url,
        currency: settings.currency,
        brand_tagline: settings.brand_tagline || "",
        hero_image: settings.hero_image || "",
        logo_image: settings.logo_image || "",
        free_gift_threshold: Number(settings.free_gift_threshold) || 0,
        free_gift_text: settings.free_gift_text || "",
        shipping_cost: Number(settings.shipping_cost) || 0,
        free_shipping_threshold: Number(settings.free_shipping_threshold) || 0,
      });
      toast.success("Ajustes guardados");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const webGroups = [
    { key: "catalogo", label: "Catálogo", items: TABS.filter((t) => ["products", "events", "testimonials", "coupons"].includes(t.key)) },
    { key: "ventas", label: "Ventas", items: TABS.filter((t) => ["orders", "subscribers", "messages"].includes(t.key)) },
    { key: "ajustes", label: "Ajustes", items: TABS.filter((t) => t.key === "settings") },
  ];

  return (
    <div className="min-h-screen bg-surface">
      <TopNav mode="web" groups={webGroups} active={tab} onSelect={setTab} preferencesKey="settings" />

      {readOnly && (
        <div data-testid="readonly-banner" className="bg-sky-50 border-b border-sky-100 text-sky-700 text-sm text-center py-2.5 px-4">
          Modo solo lectura — puedes ver la información pero no realizar cambios.
        </div>
      )}

      {/* Main */}
      <main className="p-5 md:p-10 max-w-[1600px] mx-auto">
        {/* Products */}
        {tab === "products" && (
          <div>
            <div className="flex items-center justify-between mb-8">
              <h1 className="font-serif text-3xl">Productos</h1>
              {!readOnly && (
              <button
                data-testid="add-product-btn"
                onClick={() => setModal({ type: "product", data: { ...emptyProduct } })}
                className="inline-flex items-center gap-2 bg-espresso text-cream rounded-full px-5 py-2.5 text-xs uppercase tracking-[0.15em] hover:bg-rose transition-colors"
              >
                <Plus size={15} /> Nuevo
              </button>
              )}
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {products.map((p) => (
                <div key={p.id} data-testid={`admin-product-${p.id}`} className="bg-cream border border-line rounded-xl overflow-hidden">
                  <div className="aspect-[4/3] bg-surface overflow-hidden">
                    {p.image && <img src={p.image} alt={p.name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-[10px] uppercase tracking-wider text-taupe">
                        {CATEGORIES.find((c) => c.key === p.category)?.label || p.category}
                      </span>
                      {p.is_offer && <span className="text-[10px] bg-rose text-cream px-2 rounded-full">Oferta</span>}
                      {p.is_upcoming && <span className="text-[10px] bg-espresso text-cream px-2 rounded-full">Próx.</span>}
                      {p.featured && <span className="text-[10px] bg-copper text-cream px-2 rounded-full">Destacado</span>}
                    </div>
                    <h3 className="font-serif text-lg">{p.name}</h3>
                    <p className="text-sm text-espresso mt-1">{money(p.price, currency)}</p>
                    <div className="mt-2">
                      <label className="text-[10px] uppercase tracking-wider text-taupe">Dirigido a (catálogo)</label>
                      <select data-testid={`quick-gender-${p.id}`} disabled={readOnly} value={p.gender || "unisex"} onChange={(e) => quickSetGender(p, e.target.value)}
                        className="w-full mt-1 border border-line rounded-lg px-2 py-1.5 text-xs bg-cream focus:outline-none focus:border-rose disabled:opacity-60">
                        <option value="mujer">Damas</option>
                        <option value="hombre">Caballeros</option>
                        <option value="unisex">Unisex</option>
                      </select>
                    </div>
                    {!readOnly && (
                    <div className="flex gap-2 mt-3">
                      <button
                        data-testid={`edit-product-${p.id}`}
                        onClick={() => setModal({ type: "product", data: { ...emptyProduct, ...p, offer_price: p.offer_price ?? "" } })}
                        className="flex-1 flex items-center justify-center gap-1.5 border border-line rounded-lg py-2 text-xs hover:border-espresso transition-colors"
                      >
                        <Pencil size={13} /> Editar
                      </button>
                      <button
                        data-testid={`delete-product-${p.id}`}
                        onClick={() => delProduct(p.id)}
                        className="px-3 border border-line rounded-lg text-taupe hover:text-rose hover:border-rose transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Events */}
        {tab === "events" && (
          <div>
            <div className="flex items-center justify-between mb-8">
              <h1 className="font-serif text-3xl">Eventos</h1>
              {!readOnly && (
              <button
                data-testid="add-event-btn"
                onClick={() => setModal({ type: "event", data: { ...emptyEvent } })}
                className="inline-flex items-center gap-2 bg-espresso text-cream rounded-full px-5 py-2.5 text-xs uppercase tracking-[0.15em] hover:bg-rose transition-colors"
              >
                <Plus size={15} /> Nuevo
              </button>
              )}
            </div>
            <div className="space-y-3">
              {events.map((e) => (
                <div key={e.id} data-testid={`admin-event-${e.id}`} className="bg-cream border border-line rounded-xl p-4 flex gap-4 items-center">
                  <div className="w-20 h-16 bg-surface rounded-lg overflow-hidden shrink-0">
                    {e.image && <img src={e.image} alt={e.title} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-serif text-lg">{e.title}</h3>
                    <p className="text-xs text-taupe">{e.date} · {e.location}</p>
                  </div>
                  <button data-testid={`edit-event-${e.id}`} onClick={() => setModal({ type: "event", data: { ...emptyEvent, ...e } })} className={`p-2 border border-line rounded-lg hover:border-espresso ${readOnly ? "hidden" : ""}`}>
                    <Pencil size={14} />
                  </button>
                  <button data-testid={`delete-event-${e.id}`} onClick={() => delEvent(e.id)} className={`p-2 border border-line rounded-lg text-taupe hover:text-rose hover:border-rose ${readOnly ? "hidden" : ""}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Testimonials */}
        {tab === "testimonials" && (
          <div>
            <div className="flex items-center justify-between mb-8">
              <h1 className="font-serif text-3xl">Reseñas</h1>
              {!readOnly && (
              <button
                data-testid="add-testimonial-btn"
                onClick={() => setModal({ type: "testimonial", data: { ...emptyTestimonial } })}
                className="inline-flex items-center gap-2 bg-espresso text-cream rounded-full px-5 py-2.5 text-xs uppercase tracking-[0.15em] hover:bg-rose transition-colors"
              >
                <Plus size={15} /> Nueva
              </button>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {testimonials.map((t) => (
                <div key={t.id} data-testid={`admin-testimonial-${t.id}`} className="bg-cream border border-line rounded-xl p-5">
                  <div className="flex items-center gap-1 mb-2">
                    {Array.from({ length: t.rating || 5 }).map((_, i) => (
                      <Star key={i} size={13} className="fill-copper text-copper" />
                    ))}
                    {!t.active && <span className="text-[10px] uppercase text-taupe ml-2">(oculta)</span>}
                  </div>
                  <p className="text-sm text-taupe italic">“{t.text}”</p>
                  <p className="font-serif text-lg mt-3">{t.name}</p>
                  <p className="text-xs text-taupe">{t.location}</p>
                  <div className={`flex gap-2 mt-3 ${readOnly ? "hidden" : ""}`}>
                    <button data-testid={`edit-testimonial-${t.id}`} onClick={() => setModal({ type: "testimonial", data: { ...emptyTestimonial, ...t } })} className="flex-1 flex items-center justify-center gap-1.5 border border-line rounded-lg py-2 text-xs hover:border-espresso transition-colors">
                      <Pencil size={13} /> Editar
                    </button>
                    <button data-testid={`delete-testimonial-${t.id}`} onClick={() => delTestimonial(t.id)} className="px-3 border border-line rounded-lg text-taupe hover:text-rose hover:border-rose transition-colors">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Coupons */}
        {tab === "coupons" && (
          <div>
            <div className="flex items-center justify-between mb-8">
              <h1 className="font-serif text-3xl">Cupones</h1>
              {!readOnly && (
              <button
                data-testid="add-coupon-btn"
                onClick={() => setModal({ type: "coupon", data: { ...emptyCoupon } })}
                className="inline-flex items-center gap-2 bg-espresso text-cream rounded-full px-5 py-2.5 text-xs uppercase tracking-[0.15em] hover:bg-rose transition-colors"
              >
                <Plus size={15} /> Nuevo
              </button>
              )}
            </div>
            <div className="space-y-3">
              {coupons.map((c) => (
                <div key={c.id} data-testid={`admin-coupon-${c.id}`} className="bg-cream border border-line rounded-xl p-4 flex items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <span className="font-serif text-xl tracking-widest">{c.code}</span>
                      <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${c.active ? "bg-rose text-cream" : "bg-line text-taupe"}`}>
                        {c.active ? "Activo" : "Inactivo"}
                      </span>
                    </div>
                    <p className="text-sm text-taupe mt-1">
                      {c.type === "percent" ? `${c.value}% de descuento` : `${money(c.value, currency)} de descuento`}
                      {c.min_total ? ` · mínimo ${money(c.min_total, currency)}` : ""}
                    </p>
                  </div>
                  <button data-testid={`edit-coupon-${c.id}`} onClick={() => setModal({ type: "coupon", data: { ...emptyCoupon, ...c } })} className={`p-2 border border-line rounded-lg hover:border-espresso ${readOnly ? "hidden" : ""}`}>
                    <Pencil size={14} />
                  </button>
                  <button data-testid={`delete-coupon-${c.id}`} onClick={() => delCoupon(c.id)} className={`p-2 border border-line rounded-lg text-taupe hover:text-rose hover:border-rose ${readOnly ? "hidden" : ""}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Orders */}
        {tab === "orders" && (
          <div>
            <h1 className="font-serif text-3xl mb-8">Pedidos recibidos</h1>
            {orders.length === 0 ? (
              <p className="text-taupe">Aún no hay pedidos registrados.</p>
            ) : (
              <div className="space-y-3">
                {orders.map((o) => (
                  <div key={o.id} className="bg-cream border border-line rounded-xl p-5">
                    <div className="flex justify-between flex-wrap gap-2">
                      <div>
                        <h3 className="font-serif text-lg">{o.customer_name}</h3>
                        <p className="text-xs text-taupe">{o.phone} · {o.delivery ? "Con delivery" : "Sin delivery"}</p>
                        <p className="text-xs text-taupe">{o.address}</p>
                      </div>
                      <span className="font-medium">{money(o.total, currency)}</span>
                    </div>
                    <div className="mt-3 pt-3 border-t border-line text-sm text-taupe">
                      {o.items?.map((i, idx) => (
                        <span key={idx} className="block">{i.quantity} × {i.name}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Messages */}
        {tab === "messages" && (
          <div>
            <h1 className="font-serif text-3xl mb-8">Mensajes de contacto</h1>
            {messages.length === 0 ? (
              <p className="text-taupe">No hay mensajes por ahora.</p>
            ) : (
              <div className="space-y-3">
                {messages.map((m) => (
                  <div key={m.id} className="bg-cream border border-line rounded-xl p-5">
                    <div className="flex justify-between">
                      <h3 className="font-serif text-lg">{m.name}</h3>
                      <a href={`mailto:${m.email}`} className="text-xs text-rose">{m.email}</a>
                    </div>
                    <p className="text-sm text-taupe mt-2">{m.message}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Subscribers */}
        {tab === "subscribers" && (
          <div>
            <h1 className="font-serif text-3xl mb-2">Suscriptores</h1>
            <p className="text-taupe text-sm mb-8">{subscribers.length} correos suscritos a novedades</p>
            {subscribers.length === 0 ? (
              <p className="text-taupe">Aún no hay suscriptoras.</p>
            ) : (
              <div className="bg-cream border border-line rounded-xl divide-y divide-line">
                {subscribers.map((s) => (
                  <div key={s.id} data-testid={`subscriber-${s.id}`} className="flex items-center justify-between px-5 py-3">
                    <a href={`mailto:${s.email}`} className="text-sm text-espresso hover:text-rose">{s.email}</a>
                    <span className="text-xs text-taupe">{(s.created_at || "").slice(0, 10)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Settings */}
        {tab === "settings" && settings && (
          <div className="max-w-xl">
            <h1 className="font-serif text-3xl mb-8">Ajustes de la tienda</h1>
            <div className="space-y-5 bg-cream border border-line rounded-xl p-6">
              <Field label="Número de WhatsApp (solo dígitos, con país)">
                <input data-testid="settings-whatsapp" value={settings.whatsapp_number} onChange={(e) => setSettings({ ...settings, whatsapp_number: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Correo de contacto">
                <input data-testid="settings-email" value={settings.contact_email} onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })} className={inputCls} />
              </Field>
              <Field label="URL de Instagram">
                <input data-testid="settings-instagram" value={settings.instagram_url} onChange={(e) => setSettings({ ...settings, instagram_url: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Moneda (símbolo)">
                <input data-testid="settings-currency" value={settings.currency} onChange={(e) => setSettings({ ...settings, currency: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Frase de marca (hero)">
                <input data-testid="settings-tagline" value={settings.brand_tagline || ""} onChange={(e) => setSettings({ ...settings, brand_tagline: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Imagen principal (hero)">
                <ImageUpload testid="settings-hero" value={settings.hero_image} onChange={(v) => setSettings({ ...settings, hero_image: v })} />
              </Field>
              <Field label="Logo del header">
                <ImageUpload testid="settings-logo" value={settings.logo_image} onChange={(v) => setSettings({ ...settings, logo_image: v })} />
              </Field>
              <div className="pt-2 border-t border-line">
                <p className="text-[11px] uppercase tracking-[0.15em] text-rose mb-3 mt-3">Envío 🚚</p>
                <Field label="Costo de envío (delivery)">
                  <input data-testid="settings-shipping-cost" type="number" value={settings.shipping_cost ?? 0} onChange={(e) => setSettings({ ...settings, shipping_cost: e.target.value })} className={inputCls} />
                </Field>
                <div className="mt-4" />
                <Field label="Envío gratis desde (0 = desactivado)">
                  <input data-testid="settings-free-shipping" type="number" value={settings.free_shipping_threshold ?? 0} onChange={(e) => setSettings({ ...settings, free_shipping_threshold: e.target.value })} className={inputCls} />
                </Field>
              </div>
              <div className="pt-2 border-t border-line">
                <p className="text-[11px] uppercase tracking-[0.15em] text-rose mb-3 mt-3">Regalo sorpresa 🎁</p>
                <Field label="Monto mínimo para muestra gratis (0 = desactivado)">
                  <input data-testid="settings-gift-threshold" type="number" value={settings.free_gift_threshold ?? 0} onChange={(e) => setSettings({ ...settings, free_gift_threshold: e.target.value })} className={inputCls} />
                </Field>
                <div className="mt-4" />
                <Field label="Texto del regalo">
                  <input data-testid="settings-gift-text" value={settings.free_gift_text || ""} onChange={(e) => setSettings({ ...settings, free_gift_text: e.target.value })} className={inputCls} />
                </Field>
              </div>
              <button data-testid="save-settings-btn" onClick={saveSettings} className={`w-full bg-espresso text-cream rounded-full py-3.5 text-xs uppercase tracking-[0.2em] hover:bg-rose transition-colors ${readOnly ? "hidden" : ""}`}>
                Guardar ajustes
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Modal */}
      {modal && (
        <ModalEditor
          modal={modal}
          onClose={() => setModal(null)}
          onSaveProduct={saveProduct}
          onSaveEvent={saveEvent}
          onSaveCoupon={saveCoupon}
          onSaveTestimonial={saveTestimonial}
        />
      )}
    </div>
  );
}

function ModalEditor({ modal, onClose, onSaveProduct, onSaveEvent, onSaveCoupon, onSaveTestimonial }) {
  const [data, setData] = useState(modal.data);
  const type = modal.type;
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const titleMap = { product: "producto", event: "evento", coupon: "cupón", testimonial: "reseña" };

  return (
    <div className="fixed inset-0 z-[90] bg-espresso/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-cream w-full max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-line sticky top-0 bg-cream">
          <h2 className="font-serif text-2xl">
            {data.id ? "Editar" : "Nuevo"} {titleMap[type]}
          </h2>
          <button data-testid="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          {type === "product" && (
            <>
              <Field label="Nombre">
                <input data-testid="product-name" value={data.name} onChange={(e) => set("name", e.target.value)} className={inputCls} />
              </Field>
              <Field label="Descripción">
                <textarea data-testid="product-description" rows={2} value={data.description} onChange={(e) => set("description", e.target.value)} className={inputCls} />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Categoría">
                  <select data-testid="product-category" value={data.category} onChange={(e) => set("category", e.target.value)} className={inputCls}>
                    {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </Field>
                <Field label="Precio">
                  <input data-testid="product-price" type="number" value={data.price} onChange={(e) => set("price", e.target.value)} className={inputCls} />
                </Field>
              </div>
              <Field label="Dirigido a (categoría del catálogo y test olfativo)">
                <select data-testid="product-gender" value={data.gender || "unisex"} onChange={(e) => set("gender", e.target.value)} className={inputCls}>
                  <option value="unisex">Unisex</option>
                  <option value="mujer">Damas</option>
                  <option value="hombre">Caballeros</option>
                </select>
              </Field>
              <Field label="Imagen principal">
                <ImageUpload testid="product-image" value={data.image} onChange={(v) => set("image", v)} />
              </Field>
              <Field label="Galería (fotos adicionales)">
                <MultiImageUpload testid="product-gallery" values={data.images} onChange={(v) => set("images", v)} />
              </Field>
              <Field label="Notas olfativas (para filtro y quiz)">
                <div className="flex flex-wrap gap-2">
                  {SCENT_OPTIONS.map((s) => {
                    const on = (data.scents || []).includes(s.key);
                    return (
                      <button
                        key={s.key}
                        type="button"
                        data-testid={`product-scent-${s.key}`}
                        onClick={() =>
                          set(
                            "scents",
                            on
                              ? (data.scents || []).filter((x) => x !== s.key)
                              : [...(data.scents || []), s.key]
                          )
                        }
                        className={`px-4 py-1.5 rounded-full text-xs border transition-colors ${
                          on ? "bg-rose text-cream border-rose" : "border-line text-taupe hover:border-rose"
                        }`}
                      >
                        {s.label}
                      </button>
                    );
                  })}
                </div>
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input data-testid="product-is-offer" type="checkbox" checked={data.is_offer} onChange={(e) => set("is_offer", e.target.checked)} /> En oferta
                </label>
                <Field label="Precio oferta">
                  <input data-testid="product-offer-price" type="number" value={data.offer_price} onChange={(e) => set("offer_price", e.target.value)} className={inputCls} disabled={!data.is_offer} />
                </Field>
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input data-testid="product-is-upcoming" type="checkbox" checked={data.is_upcoming} onChange={(e) => set("is_upcoming", e.target.checked)} /> Próximo lanzamiento
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input data-testid="product-featured" type="checkbox" checked={data.featured} onChange={(e) => set("featured", e.target.checked)} /> Destacado
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input data-testid="product-is-giftset" type="checkbox" checked={data.is_giftset} onChange={(e) => set("is_giftset", e.target.checked)} /> Set de regalo
                </label>
              </div>
              <button data-testid="save-product-btn" onClick={() => onSaveProduct(data)} className="w-full bg-espresso text-cream rounded-full py-3.5 text-xs uppercase tracking-[0.2em] hover:bg-rose transition-colors">
                Guardar producto
              </button>
            </>
          )}

          {type === "event" && (
            <>
              <Field label="Título"><input data-testid="event-title" value={data.title} onChange={(e) => set("title", e.target.value)} className={inputCls} /></Field>
              <Field label="Descripción"><textarea data-testid="event-description" rows={2} value={data.description} onChange={(e) => set("description", e.target.value)} className={inputCls} /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Fecha"><input data-testid="event-date" value={data.date} onChange={(e) => set("date", e.target.value)} className={inputCls} /></Field>
                <Field label="Ubicación / modo"><input data-testid="event-location" value={data.location} onChange={(e) => set("location", e.target.value)} className={inputCls} /></Field>
              </div>
              <Field label="Imagen del evento"><ImageUpload testid="event-image" value={data.image} onChange={(v) => set("image", v)} /></Field>
              <button data-testid="save-event-btn" onClick={() => onSaveEvent(data)} className="w-full bg-espresso text-cream rounded-full py-3.5 text-xs uppercase tracking-[0.2em] hover:bg-rose transition-colors">
                Guardar evento
              </button>
            </>
          )}

          {type === "coupon" && (
            <>
              <Field label="Código (ej: BIENVENIDA10)">
                <input data-testid="coupon-code" value={data.code} onChange={(e) => set("code", e.target.value.toUpperCase())} className={`${inputCls} tracking-widest`} />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Tipo de descuento">
                  <select data-testid="coupon-type" value={data.type} onChange={(e) => set("type", e.target.value)} className={inputCls}>
                    <option value="percent">Porcentaje (%)</option>
                    <option value="fixed">Monto fijo</option>
                  </select>
                </Field>
                <Field label={data.type === "percent" ? "Valor (%)" : "Valor (monto)"}>
                  <input data-testid="coupon-value" type="number" value={data.value} onChange={(e) => set("value", e.target.value)} className={inputCls} />
                </Field>
              </div>
              <Field label="Compra mínima (opcional)">
                <input data-testid="coupon-min" type="number" value={data.min_total} onChange={(e) => set("min_total", e.target.value)} className={inputCls} />
              </Field>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input data-testid="coupon-active" type="checkbox" checked={data.active} onChange={(e) => set("active", e.target.checked)} /> Cupón activo
              </label>
              <button data-testid="save-coupon-btn" onClick={() => onSaveCoupon(data)} className="w-full bg-espresso text-cream rounded-full py-3.5 text-xs uppercase tracking-[0.2em] hover:bg-rose transition-colors">
                Guardar cupón
              </button>
            </>
          )}

          {type === "testimonial" && (
            <>
              <Field label="Nombre de la clienta"><input data-testid="testimonial-name" value={data.name} onChange={(e) => set("name", e.target.value)} className={inputCls} /></Field>
              <Field label="Reseña"><textarea data-testid="testimonial-text" rows={3} value={data.text} onChange={(e) => set("text", e.target.value)} className={inputCls} /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Calificación (1-5)">
                  <select data-testid="testimonial-rating" value={data.rating} onChange={(e) => set("rating", e.target.value)} className={inputCls}>
                    {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} estrellas</option>)}
                  </select>
                </Field>
                <Field label="Ubicación (opcional)"><input data-testid="testimonial-location" value={data.location} onChange={(e) => set("location", e.target.value)} className={inputCls} /></Field>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input data-testid="testimonial-active" type="checkbox" checked={data.active} onChange={(e) => set("active", e.target.checked)} /> Mostrar en la web
              </label>
              <button data-testid="save-testimonial-btn" onClick={() => onSaveTestimonial(data)} className="w-full bg-espresso text-cream rounded-full py-3.5 text-xs uppercase tracking-[0.2em] hover:bg-rose transition-colors">
                Guardar reseña
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
